<script setup>
// Features and challenges: what a visitor will meet on the trails,
// network by network. Lots when at least half a network's trails have
// it, Some when any do. Read from each trail's ticked features, so the
// table follows the trails rather than being typed in a second time.
import { computed, inject } from 'vue';
import './trailsPage.css';
import { blockStyle, described, featureMatrix } from '../trailsPage.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const matrix = computed(() => featureMatrix(props.site_data));
const accent = computed(() => props.config.style?.accent || props.accent || 'var(--th-accent, #2f6b47)');
const LEVEL = ['—', 'Some', 'Lots'];
const editing = inject('tiqEditorCanvas', false);
</script>

<template>
    <section v-if="matrix.rows.length || editing" class="tp tfeat" :id="config.anchor_id || null" :style="[blockStyle(config), { '--tp-accent': accent }]">
        <div class="tp-wrap">
            <header v-if="config.title || config.subtitle" class="tp-head">
                <h2 v-if="config.title" class="tp-title">{{ config.title }}</h2>
                <p v-if="config.subtitle" class="tp-sub">{{ config.subtitle }}</p>
            </header>
            <p v-if="!(matrix.rows.length)" class="tp-empty">Nothing to show yet. This block reads the features ticked on your trails: open a trail in the dashboard and tick them under Trail type.</p>
            <div v-if="matrix.rows.length" class="tfeat-body">
            <div class="tfeat-scroll">
                <table class="tfeat-table">
                    <thead>
                        <tr>
                            <th scope="col" class="tp-mono tfeat-corner">Feature</th>
                            <th v-for="c in matrix.columns" :key="c.key" scope="col">{{ c.name }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="r in matrix.rows" :key="r.key">
                            <th scope="row">
                                <span class="tfeat-name">{{ r.label }}</span>
                                <span class="tfeat-desc">{{ described(config, r.key, r.description) }}</span>
                            </th>
                            <td v-for="(lv, i) in r.cells" :key="i">
                                <span class="tfeat-dot" :class="`is-${lv}`" aria-hidden="true"></span>
                                <span class="tp-mono tfeat-level">{{ LEVEL[lv] }}</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <!-- A phone is too narrow for a column per network: the
                 feature names alone filled the screen and the networks
                 sat off to the right behind a scroll nobody could see.
                 So each feature becomes a card naming its networks. -->
            <ul class="tfeat-list">
                <li v-for="r in matrix.rows" :key="r.key" class="tfeat-item">
                    <span class="tfeat-name">{{ r.label }}</span>
                    <span class="tfeat-desc">{{ described(config, r.key, r.description) }}</span>
                    <ul class="tfeat-nets">
                        <li v-for="(c, i) in matrix.columns" :key="c.key" class="tfeat-net" :class="`is-${r.cells[i]}`">
                            <span class="tfeat-dot" :class="`is-${r.cells[i]}`" aria-hidden="true"></span>
                            <span class="tfeat-netname">{{ c.name }}</span>
                            <span class="tp-mono tfeat-level">{{ LEVEL[r.cells[i]] }}</span>
                        </li>
                    </ul>
                </li>
            </ul>
            </div>
        </div>
    </section>
</template>

<style scoped>
.tfeat-scroll { overflow-x: auto; border: 1px solid var(--tp-line); border-radius: 4px; background: var(--tp-card); }
.tfeat-table { width: 100%; border-collapse: collapse; min-width: 560px; }
.tfeat-table th, .tfeat-table td { padding: 14px 20px; border-top: 1px solid var(--tp-line); text-align: center; }
.tfeat-table thead th { border-top: 0; font-weight: 700; font-size: 14px; background: var(--th-line-soft, #faf9f6); }
.tfeat-table tbody th { text-align: left; font-weight: normal; }
.tfeat-corner { text-align: left !important; color: var(--tp-faint); font-weight: 400 !important; }
.tfeat-name { display: block; font-weight: 700; font-size: 16px; }
.tfeat-desc { display: block; color: var(--tp-muted); font-size: 13.5px; margin-top: 3px; }
.tfeat-dot {
    display: block; width: 12px; height: 12px; margin: 0 auto 4px; border-radius: 50%;
    border: 1px solid var(--tp-line);
}
.tfeat-dot.is-1 { background: color-mix(in srgb, var(--tp-accent) 28%, #fff); border-color: var(--tp-accent); }
.tfeat-dot.is-2 { background: var(--tp-accent); border-color: var(--tp-accent); }
.tfeat-level { font-size: 10px; color: var(--tp-muted); letter-spacing: 0.06em; }
.tfeat-table tbody th { position: sticky; left: 0; background: var(--tp-card); }
.tfeat-body { container-type: inline-size; }
.tfeat-list { display: none; list-style: none; margin: 0; padding: 0; }
.tfeat-item { display: flex; flex-direction: column; padding: 16px 18px; border-top: 1px solid var(--tp-line); }
.tfeat-item:first-child { border-top: 0; }
.tfeat-nets { list-style: none; margin: 12px 0 0; padding: 0; display: flex; flex-wrap: wrap; gap: 6px; }
.tfeat-net {
    display: inline-flex; align-items: center; gap: 7px; padding: 6px 10px;
    border: 1px solid var(--tp-line); border-radius: 999px; font-size: 14px; line-height: 1;
}
.tfeat-net .tfeat-dot { margin: 0; width: 10px; height: 10px; flex: none; }
.tfeat-net.is-0 { color: var(--tp-faint); }
.tfeat-netname { font-weight: 600; }
/* Two networks and a feature name fit from about here; below it the
   cards take over. */
@container (max-width: 600px) {
    .tfeat-scroll { display: none; }
    .tfeat-list { display: block; border: 1px solid var(--tp-line); border-radius: 4px; background: var(--tp-card); }
}
</style>
